import { useFeatureFlag } from 'hooks/use-feature-flag';
import React, { createContext, ReactNode, useContext, useState } from 'react';

const fakeCompanyNames = [
  'ABC Pest',
  'ACME Security',
  'XYZ Plumbing',
  '123 Roofing',
  '67 HVAC',
  'Leafy Pest Control',
  'ABC Fast Quotes',
  'ZZZ Sleep Systems',
];

const fakeCampaignNames = [
  'Form Fill',
  '30+ Day',
  '60+ Day',
  '2-Day',
  'Phoenix',
  'Miami',
  'New York',
  'Los Angeles',
  'Chicago',
  'Houston',
  '7-Day',
  '15-Day',
  '45-Day',
  '90-Day',
  '120-Day',
  'Seattle',
  'San Francisco',
  'Detroit',
  'D2D',
  'Inbound',
  'Holiday',
  'Retry',
  'Summer',
  'Winter',
];

const fakeFirstNames = [
  'Alexis',
  'Angel',
  'Angel',
  'Betty',
  'Bobby',
  'Brenda',
  'Chris',
  'Christopher',
  'Cynthia',
  'Daniel',
  'Debra',
  'Delicia',
  'Dennice',
  'Dolores',
  'Dominga',
  'Donnie',
  'Edith',
  'Elle',
  'Emma',
  'Emmanuel',
  'Estevan',
  'Francisco',
  'Frank',
  'Gerald',
  'Gladys',
  'Gloria',
  'Greg',
  'Gregory',
  'Hector',
  'Helena',
  'Jacey',
  'Janet',
  'Javier',
  'Jeremy',
  'Jody',
  'Joey',
  'Johnnie',
  'Jose',
  'Joshua',
  'Karen',
  'Kate',
  'Kenneth',
  'Kory',
  'Kris',
  'Lakeeta',
  'Larry',
  'Latisha',
  'Laura',
  'Leroy',
  'Linda',
  'Linda',
  'Lisa',
  'Marcia',
  'Maria',
  'Mark',
  'Mark',
  'Mary',
  'Melanie',
  'Michael',
  'Michael',
  'Milton',
  'Mitra',
  'Montserrat',
  'Ngoc',
  'Nicholas',
  'Nora',
  'Patricia',
  'Patrick',
  'Paul',
  'Peggy',
  'Phyllis',
  'Prince',
  'Quintavious',
  'Randy',
  'Raymundo',
  'Renee',
  'Robert',
  'Roderick',
  'Rubi',
  'Sandra',
  'Sarahi',
  'Scott',
  'Scott',
  'Sean',
  'Seth',
  'Shauna',
  'Sherry',
  'Stanley',
  'Talia',
  'Teri',
  'Terrance',
  'Terry',
  'Thomas',
  'Tiffany',
  'Tina',
  'Tommy',
  'Vanessa',
  'William',
  'Yvonne',
  'Zoe',
];

interface DemoNamesContextType {
  getRandomCompanyName: (actualName: string | undefined, id: string) => string | undefined;
  getRandomCampaignName: (
    actualName: string | undefined,
    companyId: string,
    shortId: string,
  ) => string | undefined;
  getRandomFirstName: (actualName: string | undefined, id: string) => string | undefined;
  isInDemoMode: boolean;
  clearCache: () => void;
}

const DemoNamesContext = createContext<DemoNamesContextType | undefined>(undefined);

export const useDemoMode = (): DemoNamesContextType => {
  const context = useContext(DemoNamesContext);
  if (!context) {
    throw new Error('useDemoNames must be used within a DemoNamesProvider');
  }
  return context;
};

interface DemoNamesProviderProps {
  children: ReactNode;
}

export const DemoNamesProvider: React.FC<DemoNamesProviderProps> = ({ children }) => {
  const [companyNames] = useState<Map<string, string>>(() => new Map());
  const [campaignNames] = useState<Map<string, string>>(() => new Map());
  const [firstNames] = useState<Map<string, string>>(() => new Map());
  const [isInDemoMode] = useFeatureFlag('demo_mode', 'Demo mode', ['shift', 'f3']);

  const getRandomCompanyName = (actualName: string | undefined, id: string): string | undefined => {
    if (!isInDemoMode) {
      return actualName;
    }
    if (companyNames.has(id)) {
      return companyNames.get(id)!;
    }
    const companyName = fakeCompanyNames[Math.floor(Math.random() * fakeCompanyNames.length)];
    companyNames.set(id, companyName);
    return companyName;
  };

  const getRandomCampaignName = (
    actualName: string | undefined,
    companyId: string,
    id: string,
  ): string | undefined => {
    if (!isInDemoMode) {
      return actualName;
    }
    if (campaignNames.has(id)) {
      return campaignNames.get(id)!;
    }
    const companyName = getRandomCompanyName(undefined, companyId);
    const campaignName = fakeCampaignNames[Math.floor(Math.random() * fakeCampaignNames.length)];
    const campaignType = Math.random() > 0.5 ? '(Aged)' : '(RT)';
    const name = `${companyName} - ${campaignName} ${campaignType}`;
    campaignNames.set(id, name);
    return name;
  };

  const getRandomFirstName = (actualName: string | undefined, id: string): string | undefined => {
    if (!isInDemoMode) {
      return actualName;
    }
    if (firstNames.has(id)) {
      return firstNames.get(id)!;
    }
    const firstName = fakeFirstNames[Math.floor(Math.random() * fakeFirstNames.length)];
    firstNames.set(id, firstName);
    return firstName;
  };

  const clearCache = (): void => {
    companyNames.clear();
    campaignNames.clear();
  };

  const value: DemoNamesContextType = {
    getRandomCompanyName,
    getRandomCampaignName,
    getRandomFirstName,
    isInDemoMode,
    clearCache,
  };

  return <DemoNamesContext.Provider value={value}>{children}</DemoNamesContext.Provider>;
};
